import React from 'react'
import find from 'lodash/find'
import { Stack } from 'react-bootstrap'
import { useFormikContext } from 'formik'
import type { Template } from '@ztechapps/common.contract'
import type { SelectOption } from '@ztechapps/webui.input'
import { showAllFilterTemplate, useTemplateSelectOptions } from '@ztechapps/common.hook'
import { CheckboxInput, Select } from '@ztechapps/webui.input'
import { Field } from '@ztechapps/webui.form'
import { ShowIf } from '@ztechapps/webui.util'
import { useTemplateContext } from './Context'

type FilterTemplateOption = SelectOption & Template

/** A pivot template's linked filter template, and whether opening the pivot template applies it. */
export const LinkedFilterTemplateFields = () => {
  const { filterCategory } = useTemplateContext()
  const { values, setFieldValue } = useFormikContext<Template>()
  // the select's own option list comes from the same cached read; the stored id needs its option to show a name.
  // `showAllFilterTemplate` leads the list with the same sentinel the filter menu already applies on mount — a
  // distinct choice from the field's own "No filter template" placeholder (clearing the field). Rendered via
  // `Select` directly (not `TemplateSelect`, which fetches and applies its own option list last, overwriting
  // anything passed in) so this list is what actually reaches the dropdown.
  const options = [
    showAllFilterTemplate,
    ...useTemplateSelectOptions<FilterTemplateOption>({ templateCategory: filterCategory }),
  ]
  const selected = find(options, { _id: values.filterTemplateId }) ?? null

  const onChange = (option: FilterTemplateOption | null) => {
    setFieldValue('filterTemplateId', option?._id ?? null)
    if (!option) setFieldValue('autoApplyFilterTemplate', false)
  }

  return (
    <Stack gap={2}>
      <Field
        name="filterTemplateId"
        label="Linked filter template"
        help="Pick one of this report's saved filters to pair with the pivot template."
        component={Select}
        options={options}
        value={selected}
        onChange={onChange}
        getOptionLabel={(option: FilterTemplateOption) => option.name}
        getOptionValue={(option: FilterTemplateOption) => option._id}
        placeholder="No filter template"
        isClearable
      />
      <ShowIf condition={!!selected}>
        <Field
          name="autoApplyFilterTemplate"
          label="Apply the linked filter automatically"
          help="The linked filter applies whenever this pivot template opens."
          component={CheckboxInput}
          className="form-switch"
          onChange
        />
      </ShowIf>
    </Stack>
  )
}
